import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;

/**
 * Reynisfjara's hero board (2026-10-17), after the theme's own photograph (cover.webp): the black sand
 * beach under a low grey sky. At the left the cliff of basalt columns steps down to the sand, every
 * column its own grey pillar, lit on its face and dark in its joints, moss on the top; out to sea the
 * Reynisdrangar stacks stand dark against the haze; the grey sea rolls its swells in, and the surf runs
 * up the black sand in long white sheets and slides back. A gust (the pointer, a finger, the counter
 * counting) brings the rain on, as on Roraima (owner), harder the stronger it blows. Laid out by the stage's height with fixed seeds.
 */
@Component( {
  selector: 'app-hero-reynisfjara',
  template: '<canvas #canvas class="hrf-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.hrf-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroReynisfjaraComponent extends HeroMotifBase {

  private back: HTMLCanvasElement | null = null;
  private front: HTMLCanvasElement | null = null;
  private clouds: HTMLCanvasElement | null = null;
  private rocks: HTMLCanvasElement | null = null;
  private scaleK = 1;
  private horizon = 0;
  // the scene's unit, from the height alone
  private UW = 0;
  // the waves: each breaks at its own time and runs up the sand
  private waves: { phase: number; rate: number; reach: number }[] = [];
  // the stacks' feet, where the sea breaks on them: x, half width, height
  private stacks: [ number, number, number ][] = [];
  // the rain: a pool of drops, each falling only while the gust is strong enough for its rank
  private drops: { x: number; y: number; len: number; speed: number; rank: number; alpha: number }[] = [];

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.2;
  }

  protected colorsChanged(): void {
    this.back = null;
    this.front = null;
    this.clouds = null;
    this.rocks = null;
  }

  protected build(): void {
    const W = this.width;
    const H = this.height;
    this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    // the cliff, the stacks and the shore are measured by the height alone, so a wide screen never
    // widens them (owner)
    this.UW = H * 1.16;
    this.horizon = H * 0.6;
    this.back = null;
    this.front = null;
    this.clouds = null;
    const rand = this.seededLocal( 3 );
    this.drops = [];
    for ( let i = 0; i < 520; i++ ) {
      this.drops.push( {
        x: rand() * W, y: rand() * H, len: ( 14 + rand() * 22 ) * this.scaleK,
        speed: ( 700 + rand() * 500 ) * this.scaleK, rank: rand(), alpha: 0.12 + rand() * 0.2,
      } );
    }
    this.waves = [];
    for ( let i = 0; i < 5; i++ ) {
      this.waves.push( { phase: i / 5 + rand() * 0.08, rate: 1, reach: 0.7 + rand() * 0.3 } );
    }
  }

  /** Where the cliff ends: always out at the left, however wide the screen (owner). */
  private get cliffX(): number { return ( this.UW * 0.3 + 20 ) * 1.21; }

  /** The shoreline at x: from the cliff's foot it runs down and out to the right, as in the photograph. */
  private shoreAt( x: number ): number {
    const W = this.width;
    const H = this.height;
    // the sea's left end runs in under the cliff (owner)
    // where the curve begins, its corner, lies well under the cliff, so no bend shows in the water (owner)
    const t = ( x - ( this.cliffX - this.UW * 0.22 ) ) / this.UW;
    // concave (owner): falling away quickly from the cliff's foot, then easing out along the beach
    const tc = Math.max( 0, Math.min( 1, t / 1.05 ) );
    // higher up the board (owner)
    // no little wiggle near the cliff (it made an S in the water there, owner); a faint one further out
    const far = Math.max( 0, Math.min( 1, ( t - 0.4 ) / 0.3 ) );
    return H * 0.65 + ( 1 - Math.pow( 1 - tc, 3.2 ) ) * H * 0.2 + Math.sin( t * 6 ) * H * 0.004 * far;
  }

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    const scale = ctx.getTransform().a;
    if ( this.frozen ) {
      // the page's wallpaper: the rain, still, all down the page (owner)
      this.drawWallSurf( ctx );
      return;
    }
    if ( !this.back ) { this.bake( scale ); }
    if ( !this.back || !this.front || !this.clouds ) { return; }
    ctx.drawImage( this.back, 0, 0, W, H );
    // the low clouds drifting slowly to the left, wrapping round
    const off = Math.round( ( performance.now() / 1000 * 6 * this.scaleK ) % W );
    ctx.drawImage( this.clouds, -off, 0, W, H );
    ctx.drawImage( this.clouds, W - off, 0, W, H );
    this.drawSwell( ctx );
    if ( this.rocks ) { ctx.drawImage( this.rocks, 0, 0, W, H ); }
    this.drawBreakers( ctx );
    this.drawSurf( ctx );
    ctx.drawImage( this.front, 0, 0, W, H );
    this.drawRain( ctx, dt );
  }

  /** The swell out at sea: long soft bands of lighter water rolling in parallel to the shore. */
  private drawSwell( ctx: CanvasRenderingContext2D ): void {
    const W = this.width;
    const H = this.height;
    const t = this.time * W0;
    const e = this.energy;
    const [ charcoal, grey ] = this.five();
    const crest = this.mix( grey, [ 230, 235, 238 ], 0.4 );
    ctx.save();
    ctx.beginPath();
    ctx.moveTo( 0, this.horizon );
    ctx.lineTo( W, this.horizon );
    for ( let x = W; x >= 0; x -= 8 ) { ctx.lineTo( x, this.shoreAt( x ) ); }
    ctx.closePath();
    ctx.clip();
    for ( let i = 0; i < 9; i++ ) {
      // each band comes in from the horizon to the shore over the loop, nearer ones wider and brighter
      // each band follows the shore's own line (owner: waves and shore in one direction), coming
      // in from the horizon to break on it
      const f = ( ( t * 2 / ( Math.PI * 2 ) ) + i / 9 ) % 1;
      const ease = f * f;
      const k = this.scaleK;
      const near = 0.3 + 0.7 * f;
      const lineY = ( x: number ) => this.horizon + ( this.shoreAt( x ) - H * 0.02 - this.horizon ) * ease
        + Math.sin( x * 0.02 + i * 1.3 + t * 4 ) * 1.2 * k * near;
      // the crest broken into lengths that catch the light and lengths that don't, as a real swell's,
      // a darker trough under it (owner: the ruled lines looked cheap)
      for ( let x = 0; x < W; x += 10 ) {
        const lit = Math.pow( 0.5 + 0.5 * Math.sin( x * 0.011 + i * 2.1 + t * 3 ), 2.5 ) * Math.pow( 0.5 + 0.5 * Math.sin( x * 0.029 + i ), 1.5 );
        if ( lit < 0.05 ) { continue; }
        const y1 = lineY( x ), y2 = lineY( x + 10 );
        ctx.strokeStyle = this.rgba( this.mix( charcoal, [ 0, 0, 0 ], 0.3 ), 0.18 * lit * near );
        ctx.lineWidth = ( 1 + 3 * f ) * k;
        ctx.beginPath();
        ctx.moveTo( x, y1 + ( 1.5 + 3 * f ) * k );
        ctx.lineTo( x + 10, y2 + ( 1.5 + 3 * f ) * k );
        ctx.stroke();
        ctx.strokeStyle = this.rgba( crest, ( 0.08 + 0.3 * f ) * lit );
        ctx.lineWidth = ( 0.6 + 1.6 * f ) * k;
        ctx.beginPath();
        ctx.moveTo( x, y1 );
        ctx.lineTo( x + 10, y2 );
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  /**
   * The rain, as on Roraima (owner): a light drizzle at rest, heavier while the counter counts and
   * under the pointer, each drop a thin pale streak slanting with the wind.
   */
  private drawRain( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    const e = this.energy;
    const [ , grey ] = this.five();
    const tone = this.mix( grey, [ 255, 255, 255 ], 0.55 );
    const share = 0.12 + 0.88 * e;
    const slant = 0.18;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.strokeStyle = this.rgba( tone, 1 );
    ctx.lineWidth = Math.max( 0.8, 1.1 * this.scaleK );
    ctx.lineCap = 'round';
    for ( const d of this.drops ) {
      const v = d.speed * ( 1 + 0.4 * e );
      d.y += v * dt;
      d.x += v * slant * dt;
      if ( d.y - d.len > H ) { d.y = -d.len - Math.random() * H * 0.2; d.x = Math.random() * ( W + H * slant ) - H * slant; }
      if ( d.x > W + 20 ) { d.x -= W + H * slant; }
      if ( d.rank > share ) { continue; }
      ctx.globalAlpha = d.alpha * ( 0.7 + 0.5 * e );
      ctx.beginPath();
      ctx.moveTo( d.x, d.y );
      ctx.lineTo( d.x - d.len * slant, d.y - d.len );
      ctx.stroke();
    }
    ctx.restore();
  }

  /**
   * The sea breaking on the stacks (owner): every few seconds a wave hits each stack's foot and throws
   * white spray up its face, which hangs and falls back, a ring of foam round its foot after.
   */
  private drawBreakers( ctx: CanvasRenderingContext2D ): void {
    const t = this.time * W0;
    const e = this.energy;
    const k = this.scaleK;
    // a darker grey water breaking on the stacks (owner)
    const foam: RGB = [ 140, 146, 152 ];
    const y0 = this.horizon + this.height * 0.025;
    this.stacks.forEach( ( [ cx, hw, h ], i ) => {
      // the whole strike quicker (owner)
      const beats = 4;
      const f = ( ( t * beats / ( Math.PI * 2 ) ) + i * 0.37 ) % 1;
      // the burst: up fast, hanging, falling back
      // quick: it strikes fast and dies away fast (owner)
      // up quickly but easing out at the top (owner), then dying away
      // the sheet of water slides down slowly after it strikes (owner)
      // it surges up faster (owner)
      const up = f < 0.045 ? 1 - Math.pow( 1 - f / 0.045, 3 ) : Math.max( 0, 1 - ( f - 0.045 ) / 0.22 );
      // while the flung drops drop at once
      // the drops on their own quick time, not the sheet's (owner): flung up fast, down fast, gone
      const dUp = f < 0.02 ? 1 - Math.pow( 1 - f / 0.02, 2 ) : Math.max( 0, 1 - ( f - 0.02 ) / 0.04 );
      const drops = f < 0.02 ? f / 0.02 : Math.max( 0, 1 - ( f - 0.02 ) / 0.04 );
      const peak = h * 0.4;
      // well up the stack, not over it (owner)
      const rise = h * 0.4 * up;
      if ( up > 0.02 || drops > 0.02 ) {
        // a sheet of white water thrown up the stack's face, its top edge lumpy, solid at the foot
        // (owner: water, not steam)
        // the left stack's a tenth narrower (owner)
        const half = hw * ( i === 0 ? 0.945 : 1.05 );
        const sheet = ctx.createLinearGradient( 0, y0 - rise, 0, y0 );
        // smaller and fainter (owner)
        sheet.addColorStop( 0, this.rgba( foam, 0.15 * up ) );
        sheet.addColorStop( 0.5, this.rgba( foam, 0.32 * up ) );
        sheet.addColorStop( 1, this.rgba( foam, 0.42 * up ) );
        ctx.fillStyle = sheet;
        ctx.beginPath();
        ctx.moveTo( cx - half, y0 );
        for ( let j = 0; j <= 10; j++ ) {
          const u = j / 10;
          const lump = 0.55 + 0.45 * Math.sin( u * Math.PI ) + 0.15 * Math.sin( u * 17 + i * 3 );
          ctx.lineTo( cx - half + u * half * 2, y0 - rise * lump * Math.sin( u * Math.PI ) );
        }
        ctx.lineTo( cx + half, y0 );
        ctx.closePath();
        ctx.fill();
        // the drops flung out over it, arcing up and falling
        ctx.fillStyle = this.rgba( foam, 0.45 * drops );
        for ( let j = 0; j < 14; j++ ) {
          const side = ( j / 13 - 0.5 ) * 2;
          const reach = Math.min( 1, f / 0.03 );
          const px = cx + side * half * ( 0.8 + 0.7 * reach );
          // dropping quickly as they fade (owner)
          const py = y0 - peak * 1.1 * dUp * ( 1.05 - side * side * 0.5 );
          ctx.beginPath();
          ctx.arc( px + Math.sin( j * 2.3 ) * 2 * k, py + Math.cos( j * 1.7 ) * 2 * k, ( 0.6 + ( j % 3 ) * 0.4 ) * k, 0, Math.PI * 2 );
          ctx.fill();
        }
      }
      // the foam ring left round its foot
      const ring = f < 0.08 ? 0 : Math.max( 0, 1 - ( f - 0.08 ) / 0.4 );
      ctx.save();
      ctx.translate( cx, y0 + 1.5 * k );
      ctx.scale( hw * ( 1.2 + ( 1 - ring ) * 0.6 ), 2.5 * k );
      const rg = ctx.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      rg.addColorStop( 0, this.rgba( foam, 0.35 * ring ) );
      rg.addColorStop( 1, this.rgba( foam, 0 ) );
      ctx.fillStyle = rg;
      ctx.fillRect( -1, -1, 2, 2 );
      ctx.restore();
    } );
  }

  /**
   * The surf: wave after wave breaks into a white line, runs up the black sand as a sheet of foam
   * thinning at its edge, then slides back leaving the sand wet.
   */
  private drawSurf( ctx: CanvasRenderingContext2D ): void {
    const W = this.width;
    const H = this.height;
    const t = this.time * W0;
    const e = this.energy;
    const k = this.scaleK;
    const foam: RGB = [ 238, 241, 243 ];
    const count = 4;
    const x0 = this.cliffX - this.UW * 0.12;
    for ( let i = 0; i < count; i++ ) {
      const w = this.waves[ i ];
      // whole turns in the loop: 5 waves a loop, each its own fifth
      const f = ( ( t * 5 / ( Math.PI * 2 ) ) + w.phase ) % 1;
      // rushing in (0..0.45), then sliding back (0.45..1)
      const run = f < 0.45 ? Math.sin( ( f / 0.45 ) * Math.PI / 2 ) : 1 - ( f - 0.45 ) / 0.55;
      const reach = H * ( 0.035 + 0.03 * w.reach );
      // each wave fades in as it comes, never popping up at once (owner)
      const fadeIn = Math.min( 1, f / 0.15 );
      const alpha = ( f < 0.45 ? 0.85 : 0.85 * ( 1 - ( f - 0.45 ) / 0.55 ) ) * fadeIn * fadeIn * ( 3 - 2 * fadeIn );
      // the foam sheet between the breaking line out in the water and its leading edge on the sand
      const edge = ( x: number ) => this.shoreAt( x ) - H * 0.02 + run * reach + Math.sin( x * 0.012 + i * 2 + t * 3 ) * 3 * k;
      const back = ( x: number ) => this.shoreAt( x ) - H * 0.045 + run * reach * 0.5 + Math.sin( x * 0.02 + i ) * 2 * k;
      const g = ctx.createLinearGradient( 0, this.shoreAt( W / 2 ) - H * 0.05, 0, this.shoreAt( W / 2 ) + reach );
      g.addColorStop( 0, this.rgba( foam, 0.15 * alpha ) );
      g.addColorStop( 0.6, this.rgba( foam, 0.32 * alpha ) );
      g.addColorStop( 1, this.rgba( foam, 0.25 * alpha ) );
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.moveTo( x0, back( x0 ) );
      for ( let x = x0; x <= W + 4; x += 6 ) { ctx.lineTo( x, back( x ) ); }
      for ( let x = W + 4; x >= x0; x -= 6 ) { ctx.lineTo( x, edge( x ) ); }
      ctx.closePath();
      ctx.fill();
      // the bright lace at its leading edge
      ctx.strokeStyle = this.rgba( foam, 0.55 * alpha );
      ctx.lineWidth = 1.2 * k;
      ctx.beginPath();
      for ( let x = x0; x <= W + 4; x += 6 ) {
        const y = edge( x );
        if ( x === x0 ) { ctx.moveTo( x, y ); } else { ctx.lineTo( x, y ); }
      }
      ctx.stroke();
      // the white line where it breaks, out in the water, only while it comes in
      if ( f < 0.3 ) {
        ctx.strokeStyle = this.rgba( foam, 0.6 * ( 1 - f / 0.3 ) * Math.min( 1, f / 0.08 ) );
        ctx.lineWidth = 2.4 * k;
        ctx.beginPath();
        for ( let x = x0; x <= W + 4; x += 6 ) {
          const y = this.shoreAt( x ) - H * ( 0.05 - f * 0.08 );
          if ( x === x0 ) { ctx.moveTo( x, y ); } else { ctx.lineTo( x, y ); }
        }
        ctx.stroke();
      }
    }
  }

  /** The wallpaper: the rain all down the page, still, as on Roraima (owner: raindrops, not waves). */
  private drawWallSurf( ctx: CanvasRenderingContext2D ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const rand = this.seededLocal( 5 );
    const [ , grey ] = this.five();
    const tone = this.mix( grey, [ 255, 255, 255 ], 0.55 );
    const slant = 0.18;
    ctx.save();
    ctx.strokeStyle = this.rgba( tone, 1 );
    ctx.lineWidth = Math.max( 0.8, 1.1 * k );
    ctx.lineCap = 'round';
    const n = Math.round( W * H / 9000 );
    for ( let i = 0; i < n; i++ ) {
      const x = rand() * W;
      const y = rand() * H;
      const len = ( 14 + rand() * 22 ) * k;
      ctx.globalAlpha = 0.1 + rand() * 0.18;
      ctx.beginPath();
      ctx.moveTo( x, y );
      ctx.lineTo( x - len * slant, y - len );
      ctx.stroke();
    }
    ctx.restore();
  }

  /**
   * The still parts, painted once: the overcast sky, the sea to its horizon and the stacks standing in
   * it; and on a layer of their own drawn over the surf, the black sand and the basalt cliff.
   */
  private bake( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const UW = this.UW;
    const [ charcoal, grey, taupe, ochre, stone ] = this.five();
    const white: RGB = [ 255, 255, 255 ];
    const night: RGB = [ 10, 12, 15 ];
    const horizon = this.horizon;
    const canvas = () => {
      const c = document.createElement( 'canvas' );
      c.width = Math.ceil( W * scale );
      c.height = Math.ceil( H * scale );
      const g = c.getContext( '2d' )!;
      g.scale( scale, scale );
      return { c, g };
    };

    // the sky: heavy grey above, a pale band low down where the light comes through
    const { c, g } = canvas();
    const sky = g.createLinearGradient( 0, 0, 0, horizon );
    // dark enough that the hero's words read on it (owner)
    sky.addColorStop( 0, this.rgba( this.mix( charcoal, night, 0.3 ), 1 ) );
    sky.addColorStop( 0.55, this.rgba( this.mix( charcoal, grey, 0.25 ), 1 ) );
    // dark down to the sea too, so the words read there (owner)
    sky.addColorStop( 1, this.rgba( this.mix( charcoal, grey, 0.55 ), 1 ) );
    g.fillStyle = sky;
    g.fillRect( 0, 0, W, H );
    // the sea, grey-blue, darker toward us
    const sea = g.createLinearGradient( 0, horizon, 0, H * 0.85 );
    // darker water (owner)
    sea.addColorStop( 0, this.rgba( this.mix( grey, charcoal, 0.35 ), 1 ) );
    sea.addColorStop( 0.15, this.rgba( this.mix( grey, charcoal, 0.6 ), 1 ) );
    sea.addColorStop( 1, this.rgba( this.mix( charcoal, night, 0.25 ), 1 ) );
    g.fillStyle = sea;
    g.fillRect( 0, horizon, W, H - horizon );
    // the stacks, Reynisdrangar: a broad jagged one and a slender spire, dark, a little hazy
    // the stacks on a layer of their own, drawn over the swell, so they stand solid in the water (owner)
    const rk = canvas();
    const stackTone = this.mix( charcoal, night, 0.35 );
    this.stacks = [];
    const stack = ( cx: number, w: number, h: number, pts: [ number, number ][] ) => {
      // standing a little in front of the horizon, lower on the board (owner); the sea line stays
      const foot = horizon + H * 0.025;
      this.stacks.push( [ cx, w / 2, h ] );
      rk.g.fillStyle = this.rgba( this.mix( stackTone, grey, 0.12 ), 1 );
      rk.g.beginPath();
      // standing in the sea: nothing of them below the horizon's line (owner)
      rk.g.moveTo( cx - w / 2, foot );
      for ( const [ px, py ] of pts ) { rk.g.lineTo( cx + px * w, foot - py * h ); }
      rk.g.lineTo( cx + w / 2, foot );
      rk.g.closePath();
      rk.g.fill();
      // a little moss green on its shoulders, and the sea's foam at its foot
      const mg = rk.g.createLinearGradient( 0, foot - h, 0, foot );
      mg.addColorStop( 0, this.rgba( this.mix( stackTone, [ 70, 90, 60 ], 0.25 ), 0.5 ) );
      mg.addColorStop( 0.5, this.rgba( stackTone, 0 ) );
      rk.g.fillStyle = mg;
      rk.g.fill();
      rk.g.fillStyle = this.rgba( this.mix( grey, white, 0.6 ), 0.5 );
      rk.g.fillRect( cx - w * 0.55, foot - 1.5 * k, w * 1.1, 1.5 * k );
    };
    const sx = W / 2;
    // the stacks as in the owner's photograph, well to the left, close off the cliff: a broad steep
    // rock with its jagged peak at the left, and further out a slender spire with a rounded head
    // the stacks keep their own place while the cliff is moved or widened (owner)
    stack( UW * 0.3 + UW * 0.24, UW * 0.0836, H * 0.153, [ [ -0.42, 0.45 ], [ -0.36, 0.82 ], [ -0.28, 1 ], [ -0.2, 0.86 ], [ -0.12, 0.93 ],
      [ -0.04, 0.72 ], [ 0.08, 0.5 ], [ 0.22, 0.32 ], [ 0.36, 0.14 ] ] );
    stack( UW * 0.3 + UW * 0.45, UW * 0.0334, H * 0.139, [ [ -0.4, 0.35 ], [ -0.34, 0.68 ], [ -0.26, 0.9 ], [ -0.08, 1 ], [ 0.12, 0.92 ],
      [ 0.2, 0.72 ], [ 0.28, 0.45 ], [ 0.42, 0.18 ] ] );
    this.rocks = rk.c;
    // a haze lying over the sea at the horizon
    const hz = g.createLinearGradient( 0, horizon - H * 0.04, 0, horizon + H * 0.03 );
    hz.addColorStop( 0, this.rgba( this.mix( grey, white, 0.5 ), 0 ) );
    hz.addColorStop( 0.6, this.rgba( this.mix( grey, white, 0.5 ), 0.12 ) );
    hz.addColorStop( 1, this.rgba( this.mix( grey, white, 0.5 ), 0 ) );
    g.fillStyle = hz;
    g.fillRect( 0, horizon - H * 0.04, W, H * 0.07 );
    // a clearing behind the hero's words, a little darker, so they read
    for ( const b of this.blocks ) {
      g.save();
      g.translate( b.x + b.w / 2, b.y + b.h / 2 );
      g.scale( b.w / 2 + 70 * k, b.h / 2 + 40 * k );
      const cl = g.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      cl.addColorStop( 0, this.rgba( this.mix( charcoal, night, 0.3 ), 0.5 ) );
      cl.addColorStop( 1, this.rgba( charcoal, 0 ) );
      g.fillStyle = cl;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    }
    this.back = c;

    // the clouds: low grey banks, lighter where the light breaks through, on a layer that drifts
    const cl = canvas();
    const cr = this.seededLocal( 11 );
    for ( let i = 0; i < 26; i++ ) {
      const x = cr() * W;
      const y = H * ( 0.02 + cr() * 0.4 );
      const rx = ( 120 + cr() * 260 ) * k;
      const ry = ( 18 + cr() * 30 ) * k;
      // darker clouds (owner)
      const tone = cr() < 0.5 ? this.mix( charcoal, night, 0.45 ) : this.mix( charcoal, grey, 0.3 );
      for ( const dx of [ -W, 0, W ] ) {
        cl.g.save();
        cl.g.translate( x + dx, y );
        cl.g.scale( rx, ry );
        const pg = cl.g.createRadialGradient( 0, 0.2, 0, 0, 0, 1 );
        pg.addColorStop( 0, this.rgba( tone, 0.35 ) );
        pg.addColorStop( 1, this.rgba( tone, 0 ) );
        cl.g.fillStyle = pg;
        cl.g.fillRect( -1, -1, 2, 2 );
        cl.g.restore();
      }
    }
    this.clouds = cl.c;

    // the front: the black sand and the cliff of basalt columns
    const fr = canvas();
    const f = fr.g;
    // the sand: near black, a wet sheen along the water's edge, a fine grain
    const sand = f.createLinearGradient( 0, H * 0.63, 0, H );
    sand.addColorStop( 0, this.rgba( this.mix( charcoal, night, 0.2 ), 1 ) );
    sand.addColorStop( 1, this.rgba( this.mix( charcoal, night, 0.55 ), 1 ) );
    f.fillStyle = sand;
    f.beginPath();
    f.moveTo( -2, H );
    f.lineTo( -2, this.shoreAt( 0 ) );
    for ( let x = 0; x <= W + 2; x += 6 ) { f.lineTo( x, this.shoreAt( x ) + H * 0.035 ); }
    f.lineTo( W + 2, H );
    f.closePath();
    f.fill();
    const wet = f.createLinearGradient( 0, H * 0.65, 0, H * 0.78 );
    wet.addColorStop( 0, this.rgba( this.mix( grey, white, 0.2 ), 0.18 ) );
    wet.addColorStop( 1, this.rgba( grey, 0 ) );
    f.save();
    f.clip();
    f.fillStyle = wet;
    f.fillRect( 0, H * 0.63, W, H * 0.2 );
    const gr = this.seededLocal( 13 );
    for ( let i = 0; i < 1600; i++ ) {
      const x = gr() * W, y = H * 0.65 + gr() * H * 0.35;
      f.fillStyle = this.rgba( gr() < 0.5 ? this.mix( charcoal, grey, 0.35 ) : night, 0.35 );
      f.fillRect( x, y, 1.2 * k, 1.2 * k );
    }
    f.restore();

    // the cliff, as in the photograph: a weathered mass of rock above, and its face a wall of basalt
    // columns that steps down to the sand in groups, each group's columns of nearly one height with
    // ragged caps, every column lit on its face, shaded on its side, dark in its joints
    const cliffRight = this.cliffX;
    const cliffTop = ( x: number ) => {
      const t = Math.max( 0, Math.min( 1, ( x - ( cliffRight - UW * 0.5 ) ) / ( UW * 0.5 ) ) );
      // down to the sand at its end, so it finishes in a pile of short blocks, never a cut edge (owner);
      // the line itself rough and uneven, rising and dipping at random as a real cliff's does, never a
      // smooth hill (owner): big shoulders and hollows, smaller knuckles on them
      // its summit lower than before (owner)
      // a flatter summit (owner: the top climbed too steeply), falling away toward its end
      const base = H * 0.1 + Math.pow( t, 2.4 ) * ( this.shoreAt( cliffRight ) + H * 0.035 - H * 0.1 );
      const u = x / UW;
      const rough = H * ( 0.028 * Math.sin( u * 9.1 + 1.3 ) + 0.018 * Math.sin( u * 23.7 + 4.1 ) + 0.012 * Math.sin( u * 51 + 2.2 ) );
      return base + rough * Math.sin( Math.min( 1, t * 1.15 ) * Math.PI ) * 0.9;
    };
    const cliffFoot = ( x: number ) => this.shoreAt( Math.max( x, cliffRight ) ) + H * 0.035;
    const cc = this.seededLocal( 17 );
    // the mass behind the columns: dark weathered rock, its edge a little above the columns' tops
    // kept below the columns' tops, so the cliff's outline is the columns' own stepped tops (owner)
    const massEdge = ( x: number ) => cliffTop( x ) + H * 0.03;
    f.fillStyle = this.rgba( this.mix( charcoal, taupe, 0.3 ), 1 );
    f.beginPath();
    f.moveTo( -10, cliffFoot( -10 ) );
    for ( let x = -10; x <= cliffRight - 6 * k; x += 4 ) { f.lineTo( x, Math.min( massEdge( x ), cliffFoot( x ) ) ); }
    for ( let x = cliffRight - 6 * k; x >= -10; x -= 8 ) { f.lineTo( x, cliffFoot( x ) ); }
    f.closePath();
    f.fill();
    // the columns, after the owner's photograph: a dense mass of hexagonal basalt pillars in rows one
    // behind another, the back rows tall, each row nearer us a step shorter, down to the short blocks
    // piled at the foot; every pillar warm grey, its face toward the light pale and the face turning
    // away darker, no outlines, some caps broken off into dark hollows, some pillars
    // broken partway with a dark seam
    // truer colours (owner: not a pale grey): a warm grey-beige in the light, a brown-grey in shade
    // darker and toward brown (owner)
    // a tone browner again (owner)
    // darker, for a dark theme (owner)
    const lightFace = this.mix( this.mix( this.mix( stone, taupe, 0.55 ), [ 120, 92, 70 ], 0.22 ), charcoal, 0.45 );
    const darkFace = this.mix( this.mix( this.mix( taupe, [ 110, 82, 62 ], 0.25 ), charcoal, 0.7 ), night, 0.35 );
    const gap = this.mix( charcoal, night, 0.6 );
    const pillar = ( x: number, w: number, top: number, foot: number, depth: number ) => {
      const half = w * 0.5;
      // seen from the beach (owner): our eye is low, near the cliff's foot, so only pillars whose tops
      // stand below it show their caps, more of the cap the lower they are; the tops above it are seen
      // edge on or from beneath, as broken ends against the sky
      const eye = cliffFoot( 0 ) - H * 0.07;
      const below = Math.max( 0, Math.min( 1, ( top - eye ) / ( H * 0.08 ) ) );
      const cap = w * 0.12 * below;
      // some pillars stand back in shade, darker all down (the photograph's dark columns)
      const tint = cc() < 0.3 ? -0.35 : ( cc() - 0.5 ) * 0.14;
      // the farther rows paler and flatter in the grey air, as on an overcast day (not darker)
      // the air's tone kept brown, so the high rows stay the stone's colour, not white (owner)
      const airTone = this.mix( this.mix( lightFace, grey, 0.35 ), [ 120, 95, 75 ], 0.2 );
      const lf0 = this.mix( this.mix( lightFace, tint > 0 ? white : charcoal, Math.abs( tint ) ), airTone, depth * 0.3 );
      const df0 = this.mix( this.mix( darkFace, tint > 0 ? white : charcoal, Math.abs( tint ) ), airTone, depth * 0.4 );
      // the upper cliff darker (owner): the higher a pillar's top, the deeper its tone
      const high = Math.max( 0, Math.min( 1, ( H * 0.45 - top ) / ( H * 0.35 ) ) );
      const df = this.mix( df0, night, 0.32 * high );
      const lf = this.mix( lf0, night, 0.3 * high );
      // the two faces we see: the lit one on the left, the turning one on the right
      const g1 = f.createLinearGradient( 0, top, 0, foot );
      g1.addColorStop( 0, this.rgba( lf, 1 ) );
      g1.addColorStop( 0.65, this.rgba( this.mix( lf, charcoal, 0.25 ), 1 ) );
      // darker and dirtier toward the foot, where the sea and sand reach
      g1.addColorStop( 1, this.rgba( this.mix( lf, night, 0.55 ), 1 ) );
      f.fillStyle = g1;
      f.fillRect( x, top, half, foot - top );
      const g2 = f.createLinearGradient( 0, top, 0, foot );
      g2.addColorStop( 0, this.rgba( df, 1 ) );
      g2.addColorStop( 1, this.rgba( this.mix( df, night, 0.55 ), 1 ) );
      f.fillStyle = g2;
      f.fillRect( x + half, top, w - half, foot - top );
      // the darkenings of the photograph: deep shadowed recesses where a block has fallen out of the
      // face, dark at their top under the overhang and fading down
      // more of these darkenings (owner): up to two recesses a pillar
      for ( let rc = 0; rc < 2; rc++ ) {
      if ( cc() < 0.5 && foot - top > w * 1.5 ) {
        const ry = top + ( foot - top ) * ( 0.1 + cc() * 0.6 );
        const rh = Math.min( foot - ry, w * ( 0.8 + cc() * 1.6 ) );
        const rx = x + w * cc() * 0.25;
        const rw = w * ( 0.45 + cc() * 0.4 );
        const rg = f.createLinearGradient( 0, ry, 0, ry + rh );
        rg.addColorStop( 0, this.rgba( night, 0.85 ) );
        rg.addColorStop( 0.5, this.rgba( this.mix( night, charcoal, 0.5 ), 0.55 ) );
        rg.addColorStop( 1, this.rgba( charcoal, 0 ) );
        f.fillStyle = rg;
        f.beginPath();
        f.moveTo( rx, ry + rw * 0.12 );
        f.lineTo( rx + rw * 0.5, ry );
        f.lineTo( rx + rw, ry + rw * 0.1 );
        f.lineTo( rx + rw, ry + rh );
        f.lineTo( rx, ry + rh );
        f.closePath();
        f.fill();
      }
      }
      // a shadow cast down its left edge by the neighbour in front, on some
      if ( cc() < 0.4 ) {
        const cs = f.createLinearGradient( x, 0, x + w * 0.3, 0 );
        cs.addColorStop( 0, this.rgba( night, 0.5 ) );
        cs.addColorStop( 1, this.rgba( night, 0 ) );
        f.fillStyle = cs;
        f.fillRect( x, top, w * 0.3, foot - top );
      }
      // the stone's own detail: a rust-brown stain running down some, a fine speckle of pits, and a
      // dark seam where the two faces meet
      if ( cc() < 0.3 ) {
        const sx2 = x + half * ( 0.2 + cc() * 0.6 );
        const sg3 = f.createLinearGradient( 0, top, 0, top + ( foot - top ) * 0.8 );
        sg3.addColorStop( 0, this.rgba( this.mix( ochre, charcoal, 0.3 ), 0.35 ) );
        sg3.addColorStop( 1, this.rgba( ochre, 0 ) );
        f.fillStyle = sg3;
        f.fillRect( sx2, top, Math.max( 1, half * 0.35 ), ( foot - top ) * 0.8 );
      }
      f.fillStyle = this.rgba( this.mix( charcoal, night, 0.4 ), 0.35 );
      for ( let j = 0; j < 5; j++ ) {
        const px = x + cc() * w, py = top + cc() * ( foot - top );
        f.fillRect( px, py, Math.max( 0.6, 0.7 * k ), Math.max( 0.6, 0.7 * k ) );
      }
      f.fillStyle = this.rgba( this.mix( charcoal, night, 0.5 ), 0.55 );
      f.fillRect( x + half - Math.max( 0.4, w * 0.02 ), top, Math.max( 0.6, w * 0.04 ), foot - top );
      // detail lines on the stone (owner): a fine edge or two running down the faces, and short
      // fractures at a slant here and there
      f.strokeStyle = this.rgba( this.mix( charcoal, night, 0.4 ), 0.3 );
      f.lineWidth = Math.max( 0.4, 0.5 * k );
      f.beginPath();
      for ( let j = 0; j < 2; j++ ) {
        const lx = x + w * ( 0.15 + cc() * 0.7 );
        const ly = top + ( foot - top ) * cc() * 0.3;
        f.moveTo( lx, ly );
        f.lineTo( lx + ( cc() - 0.5 ) * 1.5 * k, ly + ( foot - top ) * ( 0.3 + cc() * 0.5 ) );
      }
      for ( let j = 0; j < 2; j++ ) {
        if ( cc() < 0.5 ) { continue; }
        const fx = x + w * cc() * 0.6, fy = top + ( foot - top ) * ( 0.1 + cc() * 0.8 );
        f.moveTo( fx, fy );
        f.lineTo( fx + w * 0.35, fy + ( cc() - 0.3 ) * w * 0.5 );
      }
      f.stroke();
      f.strokeStyle = this.rgba( this.mix( lf, white, 0.25 ), 0.25 );
      f.beginPath();
      const hx = x + w * ( 0.1 + cc() * 0.3 );
      f.moveTo( hx, top + 2 * k );
      f.lineTo( hx, top + ( foot - top ) * ( 0.2 + cc() * 0.4 ) );
      f.stroke();
      const hollow = cc() < 0.35;
      if ( below > 0.05 ) {
        // the cap, a hexagon seen from above, for the low ones; now and then broken into a dark hollow
        f.fillStyle = this.rgba( hollow ? this.mix( gap, darkFace, 0.25 ) : this.mix( lf, white, 0.2 ), 1 );
        f.beginPath();
        f.moveTo( x, top );
        f.lineTo( x + w * 0.22, top - cap );
        f.lineTo( x + w * 0.78, top - cap );
        f.lineTo( x + w, top );
        f.lineTo( x + w * 0.78, top + cap * 0.35 );
        f.lineTo( x + w * 0.22, top + cap * 0.35 );
        f.closePath();
        f.fill();
      } else {
        // above the eye: seen edge on, a clean top with only a faint lighter rim catching the sky
        f.fillStyle = this.rgba( this.mix( lf, [ 150, 120, 95 ], 0.25 ), 0.6 );
        f.fillRect( x, top, w, Math.max( 0.8, w * 0.05 ) );
      }
      // broken partway: a dark seam and the upper piece a hair offset
      if ( cc() < 0.3 && foot - top > w * 2.5 ) {
        const by = top + ( foot - top ) * ( 0.25 + cc() * 0.5 );
        f.fillStyle = this.rgba( gap, 0.9 );
        f.beginPath();
        f.moveTo( x, by );
        f.lineTo( x + half, by + w * 0.08 );
        f.lineTo( x + w, by - w * 0.04 );
        f.lineTo( x + w, by + w * 0.06 );
        f.lineTo( x + half, by + w * 0.16 );
        f.lineTo( x, by + w * 0.09 );
        f.closePath();
        f.fill();
      }
    };
    // many, small (owner: more of them again)
    // fewer and bigger, as if seen closer (owner)
    const rows = 8;
    // the row just behind, few pixels by few pixels, so each nearer row stays a little lower than it
    let behind = new Map<number, number>();
    let current = new Map<number, number>();
    const slot = ( x: number ) => Math.floor( x / ( 4 * k ) );
    for ( let r = 0; r < rows; r++ ) {
      // farther rows first; each nearer row a step lower, and offset by half a pillar, as hexagons pack
      const depth = 1 - r / ( rows - 1 );
      // clear steps between the rows, so they part into layers
      const drop = r * H * 0.052;
      const w0 = ( 8.7 + r * 0.75 ) * k;
      for ( let x = -14 * k + ( r % 2 ) * w0 * 0.5; x < cliffRight - r * 2 * k; ) {
        const w = w0 * ( 0.85 + cc() * 0.3 );
        // each nearer row's step wavers along the cliff, and the pillars in it differ, so the rows
        // never run as parallel lines
        const waver = Math.sin( x * 0.011 + r * 1.9 ) * H * 0.022 + Math.sin( x * 0.037 + r * 4.3 ) * H * 0.012;
        // the summit shaved flatter at the top left (owner): nothing climbs above one level there
        let top = Math.max( H * 0.13 + ( cc() - 0.5 ) * H * 0.008, cliffTop( x + w / 2 ) + drop + waver + ( cc() - 0.5 ) * H * 0.03 );
        // never above the row behind it here (owner: the layers must read in order)
        for ( let sl = slot( x ); sl <= slot( x + w ); sl++ ) {
          const b = behind.get( sl );
          if ( b !== undefined ) { top = Math.max( top, b + H * 0.004 ); }
        }
        for ( let sl = slot( x ); sl <= slot( x + w ); sl++ ) { current.set( sl, top ); }
        // feet a little uneven, sunk deeper or standing proud of the sand
        // every row stands down on the beach: raising the far rows' feet left them hanging where no
        // nearer row covers them (owner)
        const foot = cliffFoot( x ) + ( cc() - 0.3 ) * 6 * k;
        if ( top < foot - w * 0.6 ) { pillar( x, w, top, foot, depth ); }
        // set close, no outline drawn round them (owner)
        x += w;
      }
      behind = current;
      current = new Map<number, number>();
    }
    // where the cliff meets the beach (owner: not one ruled line): broken blocks fallen at its foot
    // everything on the ground at the foot goes down far to near, by where it stands (owner: in order)
    const ground: { y: number; draw: () => void }[] = [];
    const br = this.seededLocal( 23 );
    // more of them (owner)
    for ( let i = 0; i < 80; i++ ) {
      const x = br() * cliffRight * 1.04;
      const y = cliffFoot( x ) + ( br() - 0.2 ) * 5 * k;
      const bw = ( 5 + br() * 9 ) * k, bh = bw * ( 0.5 + br() * 0.5 );
      const turn = ( br() - 0.5 ) * 0.7;
      const shade = 0.25 + br() * 0.3;
      ground.push( { y, draw: () => {
        f.save();
        f.translate( x, y );
        f.rotate( turn );
        // in the cliff's own colours (owner)
        f.fillStyle = this.rgba( this.mix( lightFace, darkFace, shade ), 1 );
        f.fillRect( -bw / 2, -bh, bw, bh );
        f.fillStyle = this.rgba( this.mix( lightFace, [ 150, 120, 95 ], 0.2 ), 0.6 );
        f.fillRect( -bw / 2, -bh, bw, Math.max( 1, bh * 0.18 ) );
        f.fillStyle = this.rgba( darkFace, 0.75 );
        f.fillRect( bw * 0.15, -bh, bw * 0.35, bh );
        f.restore();
      } } );
    }
    // short broken columns all along the very foot, nearest of all, standing on the sand, above the
    // line where the cliff blends into the beach (owner)
    for ( let r = 0; r < 4; r++ ) {
      // ending before the cliff does, no stray group off at its right (owner)
      for ( let x = -8 * k + r * 4 * k; x < cliffRight - 30 * k; ) {
        const w = ( 8 + cc() * 7 ) * k;
        const foot = cliffFoot( x ) - ( 5 - r * 2 ) * k;
        const top = foot - ( 10 + cc() * 20 ) * k * ( 1 - r * 0.25 );
        const px = x;
        ground.push( { y: foot, draw: () => pillar( px, w, top, foot, 0 ) } );
        x += w * ( 0.9 + cc() * 0.5 );
      }
    }
    ground.sort( ( a, b ) => a.y - b.y ).forEach( item => item.draw() );
    this.front = fr.c;
  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The colour circle's five, or Reynisfjara's own: charcoal, grey, taupe, ochre, stone. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 42, 49, 56 ], [ 125, 132, 141 ], [ 142, 130, 118 ], [ 172, 133, 99 ], [ 177, 172, 155 ] ];
  }
}
